import type React from "react";

import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import styled, { useTheme } from "styled-components";

import { BackIcon, JointAcademyLogo } from "assets";
import { LanguageDropdown } from "routes/login/components/LanguageDropdown";
import { LOGIN_CONTAINER_WIDTH } from "routes/login/components/LoginContainer/LoginContainer";
import { AppVersion } from "shared/atoms/AppVersion";
import { Hidden } from "shared/atoms/Hidden";
import { Logo } from "shared/NavBar/components/Logo";
import { MarketTypes } from "types";
import { getSupportEmailLink, isMarket } from "utils/market";

import { MarketLoginSelector } from "../MarketLoginSelector";

interface Props {
  children?: React.ReactNode;
  backButton?: boolean;
  showLanguageDropdown?: boolean;
  showMarketLoginSelector?: boolean;
}

export const LoginPageWrapper: React.FC<Props> = ({
  children,
  backButton,
  showLanguageDropdown,
  showMarketLoginSelector,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <PageWrapper data-testid="login-page-wrapper">
      <LoginPageLogo fullLogo />

      <Hidden type="aboveTablet">
        <TopSection>
          {backButton && (
            <BackButton data-testid="back-button" to="/login/email">
              <BackIcon />
            </BackButton>
          )}

          <JointAcademyLogo />
        </TopSection>
      </Hidden>

      {children}

      <Stack
        alignItems="center"
        width={{ xs: "100%", md: LOGIN_CONTAINER_WIDTH }}
        px={{ xs: 2, md: 0 }}
        boxSizing="border-box"
      >
        {(showLanguageDropdown || showMarketLoginSelector) && (
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
            width="100%"
            sx={{ "& > *:only-child": { margin: "0 auto" } }}
          >
            {showMarketLoginSelector && !isMarket(MarketTypes.US) && <MarketLoginSelector />}
            {showLanguageDropdown && <LanguageDropdown />}
          </Stack>
        )}

        <Typography variant="subtitle1" color={theme.colors.redesign.db60} align="center" mt={2}>
          {t("common.questions")}
          <Email href={`mailto:${getSupportEmailLink()}`}>{getSupportEmailLink()}</Email>
        </Typography>

        <StyledAppVersion />
      </Stack>
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const LoginPageLogo = styled(Logo)`
  margin: 64px 0 16px 0;

  svg {
    height: 48px;
    max-height: 90px;
    max-width: 100%;
  }

  ${props => props.theme.belowBreakpoint} {
    display: none;
  }
`;

const BackButton = styled(NavLink)`
  color: ${props => props.theme.colors.primary.base};
  position: absolute;
  text-decoration: none;
  user-select: none;
  &:hover {
    cursor: pointer;
  }
  left: ${props => props.theme.spacing.S_10};
`;

const TopSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;

  min-height: 50px;
  max-height: 50px;
  width: 100%;
  padding: 4px 0 8px;
  background: ${({ theme }) => theme.colors.white};

  svg {
    max-width: 180px;
  }
`;

const Email = styled.a`
  color: ${props => props.theme.colors.redesign.db60};
  padding-left: 4px;
`;

const StyledAppVersion = styled(AppVersion)`
  margin: 16px 0 24px 0;
  color: ${({ theme }) => theme.colors.redesign.db40};
`;
